import React, { useState, useEffect } from 'react';
import {
  CurrencyDollarIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon,
  CalendarIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/authService';

export default function ContasReceberPage() {
  const [contasReceber, setContasReceber] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNova, setModalNova] = useState(false);
  const [modalEdicao, setModalEdicao] = useState(false);
  const [contaEditando, setContaEditando] = useState(null);

  const [filtros, setFiltros] = useState({
    status: '',
    cliente: '',
    account_class: '',
    startDate: '',
    endDate: '',
    search: ''
  });

  const [novaConta, setNovaConta] = useState({
    client_id: '',
    contract_id: '',
    vehicle_id: '',
    account_class: 'receita_locacao',
    cost_center: '',
    gross_amount: '',
    discount_amount: '0',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    payment_method: '',
    bank_account: '',
    document_number: '',
    description: '',
    notes: ''
  });

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    overdue: 0,
    received: 0,
    totalAmount: 0,
    pendingAmount: 0,
    overdueAmount: 0,
    receivedAmount: 0
  });

  const accountClassOptions = [
    { value: 'receita_locacao', label: 'Receita de Locação' },
    { value: 'receita_servico', label: 'Receita de Serviços' },
    { value: 'receita_multa', label: 'Multas e Penalidades' },
    { value: 'receita_manutencao', label: 'Recuperação de Manutenção' },
    { value: 'receita_financeira', label: 'Receitas Financeiras (Juros)' },
    { value: 'outras_receitas', label: 'Outras Receitas Operacionais' }
  ];

  const paymentMethodOptions = [
    { value: 'dinheiro', label: 'Dinheiro' },
    { value: 'transferencia', label: 'Transferência Bancária' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'boleto', label: 'Boleto' },
    { value: 'pix', label: 'PIX' },
    { value: 'cartao_credito', label: 'Cartão de Crédito' },
    { value: 'cartao_debito', label: 'Cartão de Débito' }
  ];

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    await Promise.all([
      carregarContasReceber(),
      carregarClientes(),
      carregarContratos(),
      carregarVeiculos()
    ]);
  };

  const carregarContasReceber = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/receivables');
      const data = response.data;
      const contas = data.data || [];
      setContasReceber(contas);
      calcularEstatisticas(contas);
    } catch (error) {
      console.error('Erro ao carregar contas a receber:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
    setLoading(false);
  };

  const carregarClientes = async () => {
    try {
      const response = await api.get('/api/clients');
      const data = response.data;
      setClientes(data.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  const carregarContratos = async () => {
    try {
      const response = await api.get('/api/contracts');
      const data = response.data;
      setContratos(data.data || []);
    } catch (error) {
      console.error('Erro ao carregar contratos:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  const carregarVeiculos = async () => {
    try {
      const response = await api.get('/api/vehicles');
      const data = response.data;
      setVeiculos(data.data || []);
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  // Normalizar status (invoices usam UPPERCASE, receivables usam lowercase)
  const normalizeStatus = (status) => {
    if (!status) return 'pending';
    const s = status.toUpperCase();
    if (['PAID', 'RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'].includes(s)) return 'received';
    if (['OVERDUE'].includes(s)) return 'overdue';
    if (['CANCELLED', 'DELETED', 'REFUNDED'].includes(s)) return 'cancelled';
    return 'pending';
  };

  const calcularEstatisticas = (contas) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    let totalAmount = 0;
    let pendingAmount = 0;
    let overdueAmount = 0;
    let receivedAmount = 0;

    let total = contas.length;
    let pending = 0;
    let overdue = 0;
    let received = 0;

    contas.forEach(conta => {
      const valor = parseFloat(conta.net_amount || conta.gross_amount || 0);
      const vencimento = new Date(conta.due_date);
      vencimento.setHours(0, 0, 0, 0);
      const status = normalizeStatus(conta.status);

      totalAmount += valor;

      if (status === 'received') {
        received++;
        receivedAmount += valor;
      } else if (status === 'overdue' || (status === 'pending' && vencimento < hoje)) {
        overdue++;
        overdueAmount += valor;
      } else if (status === 'pending') {
        pending++;
        pendingAmount += valor;
      }
    });

    setStats({
      total,
      pending,
      overdue,
      received,
      totalAmount,
      pendingAmount,
      overdueAmount,
      receivedAmount
    });
  };

  const criarConta = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/receivables', {
        ...novaConta,
        gross_amount: parseFloat(novaConta.gross_amount),
        discount_amount: parseFloat(novaConta.discount_amount || 0)
      });

      alert('Conta a receber criada com sucesso!');
      setModalNova(false);
      resetarFormulario();
      carregarContasReceber();
    } catch (error) {
      console.error('Erro:', error);
      const message = error.response?.data?.error || 'Erro ao criar conta a receber';
      alert(message);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  const editarConta = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/api/receivables/${contaEditando.id}`, {
        ...novaConta,
        gross_amount: parseFloat(novaConta.gross_amount),
        discount_amount: parseFloat(novaConta.discount_amount || 0)
      });

      alert('Conta a receber atualizada com sucesso!');
      setModalEdicao(false);
      setContaEditando(null);
      resetarFormulario();
      carregarContasReceber();
    } catch (error) {
      console.error('Erro:', error);
      const message = error.response?.data?.error || 'Erro ao atualizar conta a receber';
      alert(message);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  const marcarComoRecebida = async (contaId) => {
    if (!confirm('Confirma o recebimento desta conta?')) return;

    try {
      await api.put(`/api/receivables/${contaId}/status`, {
        status: 'received',
        received_at: new Date().toISOString()
      });

      alert('Recebimento confirmado!');
      carregarContasReceber();
    } catch (error) {
      console.error('Erro:', error);
      const message = error.response?.data?.error || 'Erro ao confirmar recebimento';
      alert(message);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  const excluirConta = async (contaId) => {
    if (!confirm('Confirma a exclusão desta conta a receber? Esta ação não pode ser desfeita.')) return;

    try {
      await api.delete(`/api/receivables/${contaId}`);

      alert('Conta a receber excluída com sucesso!');
      carregarContasReceber();
    } catch (error) {
      console.error('Erro:', error);
      const message = error.response?.data?.error || 'Erro ao excluir conta a receber';
      alert(message);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  const abrirEdicao = (conta) => {
    setContaEditando(conta);
    setNovaConta({
      client_id: conta.client_id || '',
      contract_id: conta.contract_id || '',
      vehicle_id: conta.vehicle_id || '',
      account_class: conta.account_class || 'receita_locacao',
      cost_center: conta.cost_center || '',
      gross_amount: conta.gross_amount || '',
      discount_amount: conta.discount_amount || '0',
      issue_date: conta.issue_date ? conta.issue_date.split('T')[0] : '',
      due_date: conta.due_date ? conta.due_date.split('T')[0] : '',
      payment_method: conta.payment_method || '',
      bank_account: conta.bank_account || '',
      document_number: conta.document_number || '',
      description: conta.description || '',
      notes: conta.notes || ''
    });
    setModalEdicao(true);
  };

  const resetarFormulario = () => {
    setNovaConta({
      client_id: '',
      contract_id: '',
      vehicle_id: '',
      account_class: 'receita_locacao',
      cost_center: '',
      gross_amount: '',
      discount_amount: '0',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
      payment_method: '',
      bank_account: '',
      document_number: '',
      description: '',
      notes: ''
    });
  };

  const getStatusInfo = (status, dueDate) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const vencimento = new Date(dueDate);
    vencimento.setHours(0, 0, 0, 0);
    const normalized = normalizeStatus(status);

    if (normalized === 'received') {
      return { color: '#27ae60', icon: CheckCircleIcon, text: 'Recebida' };
    } else if (normalized === 'cancelled') {
      return { color: '#95a5a6', icon: ExclamationTriangleIcon, text: 'Cancelada' };
    } else if (normalized === 'overdue' || (normalized === 'pending' && vencimento < hoje)) {
      return { color: '#e74c3c', icon: ExclamationTriangleIcon, text: 'Vencida' };
    } else if (normalized === 'pending') {
      return { color: '#f39c12', icon: ClockIcon, text: 'Pendente' };
    }
    return { color: '#95a5a6', icon: ClockIcon, text: status || 'Indefinido' };
  };

  const getAccountClassLabel = (value) => {
    const option = accountClassOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const formatarData = (dateString) => {
    if (!dateString) return '';
    // Evita problema de timezone ao trabalhar apenas com a string da data
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor || 0);
  };

  const calcularValorLiquido = () => {
    const bruto = parseFloat(novaConta.gross_amount) || 0;
    const desconto = parseFloat(novaConta.discount_amount) || 0;
    return bruto - desconto;
  };

  const contasFiltradas = contasReceber.filter(conta => {
    if (filtros.status && normalizeStatus(conta.status) !== filtros.status) return false;
    if (filtros.cliente && conta.client_id !== filtros.cliente) return false;
    if (filtros.account_class && conta.account_class !== filtros.account_class) return false;
    if (filtros.startDate && new Date(conta.due_date) < new Date(filtros.startDate)) return false;
    if (filtros.endDate && new Date(conta.due_date) > new Date(filtros.endDate)) return false;
    if (filtros.search) {
      const searchLower = filtros.search.toLowerCase();
      const matchesDescription = conta.description?.toLowerCase().includes(searchLower);
      const matchesClient = conta.client_name?.toLowerCase().includes(searchLower);
      const matchesReference = conta.reference_code?.toLowerCase().includes(searchLower);
      if (!matchesDescription && !matchesClient && !matchesReference) return false;
    }
    return true;
  });

  const limparFiltros = () => {
    setFiltros({
      status: '',
      cliente: '',
      account_class: '',
      startDate: '',
      endDate: '',
      search: ''
    });
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contas a Receber</h1>
            <p className="text-gray-600">Gestão completa de recebimentos e receitas</p>
          </div>
          <div className="flex items-center space-x-3">
            <BanknotesIcon className="w-8 h-8 text-green-600" />
            <button
              onClick={() => setModalNova(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Nova Conta</span>
            </button>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <DocumentTextIcon className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">{formatarMoeda(stats.totalAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <ClockIcon className="w-8 h-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pendentes</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-sm text-gray-500">{formatarMoeda(stats.pendingAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Vencidas</p>
              <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
              <p className="text-sm text-gray-500">{formatarMoeda(stats.overdueAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <CheckCircleIcon className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Recebidas</p>
              <p className="text-2xl font-bold text-green-600">{stats.received}</p>
              <p className="text-sm text-gray-500">{formatarMoeda(stats.receivedAmount)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filtros.search}
                onChange={(e) => setFiltros({...filtros, search: e.target.value})}
              />
            </div>
          </div>

          <select
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={filtros.status}
            onChange={(e) => setFiltros({...filtros, status: e.target.value})}
          >
            <option value="">Todos os Status</option>
            <option value="pending">Pendente</option>
            <option value="received">Recebida</option>
          </select>

          <select
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={filtros.account_class}
            onChange={(e) => setFiltros({...filtros, account_class: e.target.value})}
          >
            <option value="">Todas as Classes</option>
            {accountClassOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={filtros.cliente}
            onChange={(e) => setFiltros({...filtros, cliente: e.target.value})}
          >
            <option value="">Todos os Clientes</option>
            {clientes.map(cliente => (
              <option key={cliente.id} value={cliente.id}>
                {cliente.name}
              </option>
            ))}
          </select>

          <input
            type="date"
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={filtros.startDate}
            onChange={(e) => setFiltros({...filtros, startDate: e.target.value})}
            placeholder="Data inicial"
          />

          <input
            type="date"
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={filtros.endDate}
            onChange={(e) => setFiltros({...filtros, endDate: e.target.value})}
            placeholder="Data final"
          />

          <button
            onClick={limparFiltros}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center justify-center space-x-2"
          >
            <FunnelIcon className="w-4 h-4" />
            <span>Limpar</span>
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Contas a Receber ({contasFiltradas.length})
          </h3>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : contasFiltradas.length === 0 ? (
          <div className="text-center py-12">
            <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma conta encontrada</h3>
            <p className="mt-1 text-sm text-gray-500">
              {contasReceber.length === 0 
                ? 'Comece criando sua primeira conta a receber.'
                : 'Tente ajustar os filtros de busca.'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Referência / Cliente
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Veículo
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Valor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Vencimento
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Boleto / PIX
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contasFiltradas.map((conta) => {
                  const statusInfo = getStatusInfo(conta.status, conta.due_date);
                  const StatusIcon = statusInfo.icon;

                  return (
                    <tr key={conta.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {conta.invoice_number || conta.reference_code || conta.document_number || '-'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {conta.client_name || 'Cliente não informado'}
                          </div>
                          {conta.source === 'invoice' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                              Fatura Contrato
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {conta.vehicle_model ? (
                          <div className="flex items-center">
                            <TruckIcon className="w-4 h-4 text-gray-400 mr-1" />
                            <div>
                              <div className="text-sm text-gray-900">{conta.vehicle_model}</div>
                              <div className="text-xs text-gray-500">{conta.vehicle_plate}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="text-sm font-bold text-gray-900">
                          {formatarMoeda(conta.net_amount || conta.gross_amount)}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatarData(conta.due_date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{
                          backgroundColor: `${statusInfo.color}20`,
                          color: statusInfo.color
                        }}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusInfo.text}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          {conta.boleto_url && (
                            <a
                              href={conta.boleto_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors"
                              title="Abrir Boleto"
                            >
                              <BanknotesIcon className="w-3 h-3 mr-1" />
                              Boleto
                            </a>
                          )}
                          {conta.pix_qrcode && (
                            <button
                              onClick={() => { navigator.clipboard.writeText(conta.pix_copy_paste || ''); alert('PIX copiado!'); }}
                              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                              title="Copiar PIX"
                            >
                              PIX
                            </button>
                          )}
                          {!conta.boleto_url && !conta.pix_qrcode && (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-1">
                          {normalizeStatus(conta.status) === 'pending' && conta.source !== 'invoice' && (
                            <button
                              onClick={() => marcarComoRecebida(conta.id)}
                              className="text-green-600 hover:text-green-900"
                              title="Marcar como Recebida"
                            >
                              <CheckCircleIcon className="w-4 h-4" />
                            </button>
                          )}
                          {conta.source !== 'invoice' && (
                            <>
                              <button
                                onClick={() => abrirEdicao(conta)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Editar"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => excluirConta(conta.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Excluir"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Nova/Editar Conta */}
      {(modalNova || modalEdicao) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {modalNova ? 'Nova Conta a Receber' : 'Editar Conta a Receber'}
              </h3>
              <button
                onClick={() => { 
                  modalNova ? setModalNova(false) : setModalEdicao(false); 
                  setContaEditando(null);
                  resetarFormulario(); 
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={modalNova ? criarConta : editarConta} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Cliente */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cliente *
                  </label>
                  <select
                    value={novaConta.client_id}
                    onChange={(e) => setNovaConta({...novaConta, client_id: e.target.value})}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Selecione</option>
                    {clientes.map(cliente => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Classe Financeira */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Classe Financeira (DRE) *
                  </label>
                  <select
                    value={novaConta.account_class}
                    onChange={(e) => setNovaConta({...novaConta, account_class: e.target.value})}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {accountClassOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Contrato */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contrato (opcional)
                  </label>
                  <select
                    value={novaConta.contract_id}
                    onChange={(e) => setNovaConta({...novaConta, contract_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Nenhum</option>
                    {contratos.map(contrato => (
                      <option key={contrato.id} value={contrato.id}>
                        Contrato #{contrato.id}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Veículo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Veículo (opcional)
                  </label>
                  <select
                    value={novaConta.vehicle_id}
                    onChange={(e) => setNovaConta({...novaConta, vehicle_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Nenhum</option>
                    {veiculos.map(veiculo => (
                      <option key={veiculo.id} value={veiculo.id}>
                        {veiculo.model} - {veiculo.license_plate}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Valor Bruto */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor Bruto *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={novaConta.gross_amount}
                    onChange={(e) => setNovaConta({...novaConta, gross_amount: e.target.value})}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Desconto */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Desconto
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={novaConta.discount_amount}
                    onChange={(e) => setNovaConta({...novaConta, discount_amount: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Valor Líquido: {formatarMoeda(calcularValorLiquido())}
                  </p>
                </div>

                {/* Data de Emissão */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Emissão *
                  </label>
                  <input
                    type="date"
                    value={novaConta.issue_date}
                    onChange={(e) => setNovaConta({...novaConta, issue_date: e.target.value})}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Data de Vencimento */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Vencimento *
                  </label>
                  <input
                    type="date"
                    value={novaConta.due_date}
                    onChange={(e) => setNovaConta({...novaConta, due_date: e.target.value})}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Centro de Custo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Centro de Custo
                  </label>
                  <input
                    type="text"
                    value={novaConta.cost_center}
                    onChange={(e) => setNovaConta({...novaConta, cost_center: e.target.value})}
                    placeholder="Ex: Filial SP, Projeto X"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Forma de Pagamento */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Forma de Pagamento
                  </label>
                  <select
                    value={novaConta.payment_method}
                    onChange={(e) => setNovaConta({...novaConta, payment_method: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Selecione</option>
                    {paymentMethodOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <input
                  type="text"
                  value={novaConta.description}
                  onChange={(e) => setNovaConta({...novaConta, description: e.target.value})}
                  required
                  placeholder="Ex: Locação mensal veículo X"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Observações */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={novaConta.notes}
                  onChange={(e) => setNovaConta({...novaConta, notes: e.target.value})}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Botões */}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => { 
                    modalNova ? setModalNova(false) : setModalEdicao(false);
                    setContaEditando(null); 
                    resetarFormulario(); 
                  }}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg"
                >
                  {modalNova ? 'Criar Conta' : 'Atualizar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}